// Email service for sending campaign episode notifications
// Uses EmailJS for client-side email sending
// Install with: npm install @emailjs/browser

import emailjs from '@emailjs/browser';

// EmailJS configuration - you'll need to set these up in your EmailJS dashboard
const SERVICE_ID = 'service_ivgqe45'; // Replace with your EmailJS service ID from Step 2
const TEMPLATE_ID = 'template_11ey03i'; // Replace with your EmailJS template ID from Step 3
const PUBLIC_KEY = 'MeYeS48rZqnt7Fg_O'; // Replace with your EmailJS public key from Step 4 (without 'public_' prefix)

/**
 * Initialize EmailJS with your public key
 */
export const initializeEmailJS = () => {
  emailjs.init(PUBLIC_KEY);
};

/**
 * Send campaign episode creation notification emails
 * @param {object} episode - The campaign episode object
 * @param {Array} users - Array of users to notify
 * @param {object} creator - The user who created the episode
 * @returns {Promise<Array>} Results of email sending attempts
 */
export const sendCampaignEpisodeNotifications = async (episode, users, creator) => {
  if (!episode || !users || users.length === 0) {
    console.log('No episode data or users provided for email notifications');
    return [];
  }

  const emailPromises = users.map(user => sendEpisodeEmail(episode, user, creator));
  
  try {
    const results = await Promise.allSettled(emailPromises);
    
    // Log results
    const successful = results.filter(result => result.status === 'fulfilled').length;
    const failed = results.filter(result => result.status === 'rejected').length;
    
    console.log(`Campaign episode email notifications: ${successful} sent, ${failed} failed`);
    
    return results;
  } catch (error) {
    console.error('Error sending campaign episode notifications:', error);
    throw error;
  }
};

/**
 * Send individual episode notification email
 * @param {object} episode - The campaign episode object
 * @param {object} user - The user to notify
 * @param {object} creator - The user who created the episode
 * @returns {Promise}
 */
const sendEpisodeEmail = async (episode, user, creator) => {
  const templateParams = {
    to_email: user.email,
    to_name: user.name,
    episode_title: episode.title,
    episode_number: episode.episodeNumber || 'N/A',
    episode_description: episode.description || 'No description provided',
    creator_name: creator.name || creator.displayName || 'Game Master',
    created_date: episode.createdAt?.toDate ? episode.createdAt.toDate().toLocaleDateString() : new Date().toLocaleDateString(),
    app_url: window.location.origin,
    episode_url: `${window.location.origin}/campaign-episodes/${episode.id}`
  };

  try {
    const response = await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams);
    console.log(`Email sent successfully to ${user.email}:`, response);
    return response;
  } catch (error) {
    console.error(`Failed to send email to ${user.email}:`, error);
    throw error;
  }
};

/**
 * Test email sending functionality
 * @param {string} testEmail - Email address to send test to
 * @returns {Promise}
 */
export const sendTestEmail = async (testEmail) => {
  const templateParams = {
    to_email: testEmail,
    to_name: 'Test User',
    episode_title: 'Test Episode: Email Configuration',
    episode_number: '0',
    episode_description: 'This is a test email to verify your email notification setup is working correctly.',
    creator_name: 'System Administrator',
    created_date: new Date().toLocaleDateString(),
    app_url: window.location.origin,
    episode_url: `${window.location.origin}/campaign-episodes`
  };

  try {
    const response = await emailjs.send(SERVICE_ID, TEMPLATE_ID, templateParams);
    console.log('Test email sent successfully:', response);
    return response;
  } catch (error) {
    console.error('Failed to send test email:', error);
    throw error;
  }
};
